import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, ArrowUpRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { PageSEO, ContactPageSchema, BreadcrumbSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Contact", path: "/contact" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent successfully!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen pt-36 pb-28 px-6 md:px-12 lg:px-24">
      <PageSEO
        title="Contact"
        description="Get in touch with Mist Media for collaborations, project inquiries, or to learn more about our film, television, and digital productions. Based in Los Angeles, CA."
        path="/contact"
        schema={[ContactPageSchema(), BreadcrumbSchema(breadcrumbs)]}
      />

      <div className="max-w-[1400px] mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
            <li><Link to="/" className="text-[#3f3f46] hover:text-white transition-colors">Home</Link></li>
            <li><ChevronRight size={10} className="text-[#27272A]" /></li>
            <li className="text-[#71717A]">Contact</li>
          </ol>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* Left - Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
              <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">Get in Touch</p>
            </div>
            <h1 className="font-['Syne'] text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-[-0.04em] text-white mb-8">
              Contact
            </h1>

            <p className="text-sm text-[#71717A] leading-relaxed mb-14 max-w-md">
              Interested in collaborating or learning more about our projects? We would love to hear from you.
            </p>

            <address className="space-y-8 not-italic">
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-none bg-[#0A0A0A] border border-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Mail size={14} className="text-[#52525B]" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-1">Email</p>
                  <a href="mailto:hello@mistmedia.com" className="text-sm text-white hover:text-[#38BDF8] transition-colors inline-flex items-center gap-1 group">
                    hello@mistmedia.com
                    <ArrowUpRight size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-5">
                <div className="w-10 h-10 rounded-none bg-[#0A0A0A] border border-[#1A1A1A] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <MapPin size={14} className="text-[#52525B]" />
                </div>
                <div>
                  <p className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-1">Location</p>
                  <p className="text-sm text-white">Los Angeles, CA</p>
                </div>
              </div>
            </address>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="lg:col-span-7"
          >
            <div className="bg-[#0A0A0A] border border-[#141414] p-8 md:p-10 rounded-none">
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="contact-name" className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Name *</label>
                    <Input
                      id="contact-name"
                      data-testid="contact-name-input"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Your name"
                      required
                      className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] focus:border-[#38BDF8] rounded-none h-11"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Email *</label>
                    <Input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                      className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] focus:border-[#38BDF8] rounded-none h-11"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Subject</label>
                  <Input
                    id="contact-subject"
                    data-testid="contact-subject-input"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    placeholder="What is this about?"
                    className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] focus:border-[#38BDF8] rounded-none h-11"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="text-[9px] uppercase tracking-[0.2em] font-bold text-[#3f3f46] mb-2 block">Message *</label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder="Tell us about your project or inquiry..."
                    rows={5}
                    required
                    className="bg-[#050505] border-[#1A1A1A] text-white placeholder:text-[#3f3f46] focus:border-[#38BDF8] rounded-none resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={sending}
                  className="btn-press bg-white text-[#050505] hover:bg-[#38BDF8] font-bold uppercase tracking-[0.2em] text-[11px] px-8 py-3 h-auto rounded-none transition-all duration-300"
                >
                  {sending ? "Sending..." : (
                    <>Send Message <Send size={12} className="ml-2" /></>
                  )}
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
